import { useState, useEffect } from "react";
import { ref, onValue, off } from "firebase/database";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import useMoveTo from "../constants/navigation";
import { ROUTES } from "../App";
import { BsGeoAlt, BsBuilding } from "react-icons/bs";
import { CiSearch } from "react-icons/ci";

const FindJob = () => {
  const navigate = useMoveTo();
  const { currentUser } = useUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const jobsRef = ref(database, "Jobs");
    onValue(
      jobsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const list = Object.entries(data)
            .map(([id, job]) => ({ id, ...job }))
            .filter(
              (job) =>
                job.status === "active" && job.hostId !== currentUser?.uid,
            )
            .sort((a, b) => b.createdAt - a.createdAt);
          setJobs(list);
        } else {
          setJobs([]);
        }
        setLoading(false);
      },
      (error) => {
        console.error("FindJob fetch error:", error);
        setLoading(false);
      },
    );
    return () => off(jobsRef);
  }, [currentUser?.uid]);

  const filteredJobs = jobs.filter((job) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      job.title?.toLowerCase().includes(q) ||
      job.companyName?.toLowerCase().includes(q) ||
      job.hostName?.toLowerCase().includes(q) ||
      job.location?.toLowerCase().includes(q) ||
      job.role?.toLowerCase().includes(q);
    const matchesFilter = activeFilter === "all" || job.type === activeFilter;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-[#1A1A1A] text-black dark:text-white">
      <MobileHeader title="Find a Job" isBackBtn />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 min-h-0 overflow-y-auto pt-5 pb-24 md:pb-20 px-6 bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
          {/* Title */}
          <div className="flex items-center justify-between">
            <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
              Find a Job
            </h2>
            <div className="w-5" />
          </div>

          {/* Search */}
          <div className="relative w-full md:mt-6 mt-0">
            <input
              type="text"
              placeholder="Search jobs, companies, locations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="rounded-xl pl-5 pr-10 py-3 bg-[#F5F9FE] dark:bg-white
                                       placeholder-[#6b7280] outline-none w-full text-[#262626]
                                       dark:text-[#262626] border border-gray-200 dark:border-none"
            />
            <CiSearch className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-500 text-2xl" />
          </div>

          {/* Filter toggles */}
          <div className="flex gap-2 mt-4 mb-2">
            {[
              { key: "all", label: "All" },
              { key: "freelance", label: "Freelance" },
              { key: "business", label: "Business Request" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition ${
                  activeFilter === f.key
                    ? "bg-[#327691] text-white"
                    : "bg-transparent text-black dark:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Job list */}
          <div
            className={`mt-4 grid grid-cols-1 ${filteredJobs.length === 0 ? "md:grid-cols-1" : "md:grid-cols-2"} gap-3`}
          >
            {loading ? (
              <ShimmerLoader />
            ) : filteredJobs.length === 0 ? (
              <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                {searchQuery
                  ? "No jobs match your search."
                  : "No active job posts yet. Check back soon."}
              </div>
            ) : (
              filteredJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  onClick={() => navigate(`${ROUTES.JOBS_APPLY}/${job.id}`)}
                />
              ))
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

const ShimmerLoader = () => (
  <div className="grid grid-cols-1 gap-3">
    {[...Array(5)].map((_, i) => (
      <div
        key={i}
        className="animate-pulse p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#242424]"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="w-32 h-4 bg-gray-300 dark:bg-gray-700 rounded" />
          <div className="w-20 h-5 bg-gray-300 dark:bg-gray-700 rounded-lg" />
        </div>
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-4 h-4 bg-gray-300 dark:bg-gray-700 rounded" />
          <div className="w-40 h-3 bg-gray-300 dark:bg-gray-700 rounded" />
        </div>
        <div className="flex items-center space-x-3 mb-3">
          <div className="w-4 h-4 bg-gray-300 dark:bg-gray-700 rounded" />
          <div className="w-32 h-3 bg-gray-300 dark:bg-gray-700 rounded" />
        </div>
        <div className="flex items-center justify-between">
          <div className="w-28 h-3 bg-gray-300 dark:bg-gray-700 rounded" />
          <div>
            <div className="w-14 h-6 bg-gray-300 dark:bg-gray-700 rounded-full" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const JobCard = ({ job, onClick }) => (
  <button
    onClick={onClick}
    className="w-full text-left p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#242424]
                   hover:border-[#327691] hover:shadow-sm transition-all duration-200"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-bold text-base line-clamp-1">{job.title}</h3>
          <span className="text-xs px-2 py-1 rounded-full bg-[#327691]/10 text-[#327691] capitalize whitespace-nowrap">
            {job.type === "business" ? "Business" : "Freelance"}
          </span>
        </div>

        <div className="flex items-center gap-1.5 mt-2 text-sm text-gray-600 dark:text-gray-300">
          <BsBuilding className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="line-clamp-1">
            {job.companyName || job.hostName}
          </span>
        </div>

        <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-600 dark:text-gray-300">
          <BsGeoAlt className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="line-clamp-1">{job.location}</span>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
            {job.role}
          </p>
          <span className="font-semibold text-[#327691] text-sm whitespace-nowrap">
            {job.payRate}
          </span>
        </div>
      </div>
    </div>
  </button>
);

export default FindJob;
